import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from './Icon';

type Tone = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  tone: Tone;
  title: string;
  body?: string;
}

interface ToastApi {
  push: (t: Omit<Toast, 'id'>) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++seq.current;
    setItems((xs) => [...xs, { ...t, id }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), t.tone === 'error' ? 8000 : 4500);
  }, []);

  // Promotion has to be requested; an element with `popover` is hidden until it is shown.
  const layer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = layer.current;
    if (!el || typeof el.showPopover !== 'function') return;
    try {
      const open = el.matches(':popover-open');
      if (!items.length) {
        if (open) el.hidePopover();
        return;
      }
      // Re-promote on every push, not only the first. The top layer is ordered by promotion, so
      // a dialog opened while a toast was already showing sits above it — and `showPopover()` on
      // an open popover throws, which the catch below swallowed, leaving every later toast stuck
      // behind the dialog's backdrop. Closing first puts the layer back on top.
      if (open) el.hidePopover();
      el.showPopover();
    } catch {
      /* popover unsupported: the fixed position still applies */
    }
  }, [items]);

  /**
   * The newest toast, announced from a region OUTSIDE the popover.
   *
   * A hidden popover is `display: none`, so its subtree is not in the accessibility tree. The
   * first toast's text was therefore inserted before the live region existed, and a region that
   * appears already populated is typically not announced at all.
   */
  const latest = items[items.length - 1];

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (title, body) => push({ tone: 'success', title, body }),
      error: (title, body) => push({ tone: 'error', title, body }),
      info: (title, body) => push({ tone: 'info', title, body }),
    }),
    [push],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {/* Always rendered, and never inside the popover — see the note on `latest`. */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {latest ? [latest.title, latest.body].filter(Boolean).join('. ') : ''}
      </div>
      {/*
        Promoted to the top layer, not merely given a high z-index.
        `Dialog` opens with `showModal()`, which puts the dialog AND its ::backdrop in the top
        layer — above every stacking context on the page, whatever the z-index. A toast raised by
        an action taken inside a modal therefore rendered behind the scrim, dimmed and unreadable.
        A manual popover is promoted the same way, so the two sit in the same layer.
      */}
      <div
        ref={layer}
        popover="manual"
        className="fixed inset-auto bottom-5 right-5 z-[100] m-0 p-0 border-0 bg-transparent overflow-visible flex flex-col gap-2.5 w-[360px] max-w-[calc(100vw-40px)]"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className={cn(
              'card p-3.5 shadow-pop flex gap-3 items-start animate-[fadeIn_150ms_ease-out]',
              t.tone === 'error' && 'border-callout-error-border',
              t.tone === 'success' && 'border-callout-success-border',
            )}
          >
            <Icon
              name={t.tone === 'success' ? 'check' : t.tone === 'error' ? 'alert' : 'info'}
              size={18}
              className={cn(
                'mt-0.5 shrink-0',
                t.tone === 'success' && 'text-status-autorizada',
                t.tone === 'error' && 'text-status-error',
                t.tone === 'info' && 'text-primary',
              )}
            />
            <div className="min-w-0">
              <div className="text-sm font-bold">{t.title}</div>
              {t.body && <div className="text-[13px] text-muted mt-0.5 break-words">{t.body}</div>}
            </div>
            <button
              type="button"
              className="ml-auto text-muted-3 hover:text-ink"
              onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}
              aria-label="×"
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
